import type { Column } from 'react-table';
import { ContextMenuButton, TableCell, TableHeader } from 'redesign';
import { IconFlexPoolOnlyJob, IconWarning } from 'assets/img';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ClaimantCellProps, ClaimantColumnDefinition } from './claimant-table.types';
import { useGetShiftDetailsColumnDefinitionBase } from '../../shared/columns/use-get-shift-details-column-definition-base';

export const useGetClaimantColumnDefinition: () => Column<ClaimantColumnDefinition>[] = () => {
  const [flexWorkerImage, fw] = useGetShiftDetailsColumnDefinitionBase();
  const { t } = useTranslation('hq-screen-specific');

  const flexPoolIndicator: Column<ClaimantColumnDefinition> = {
    Header: '',
    accessor: 'flexPoolIndicator',
    Cell: FlexPoolIndicatorCell,
  };
  const email: Column<ClaimantColumnDefinition> = {
    Header: () => (
      <TableHeader>
        {t('shifts.placements.table_headers.email', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'email',
    Cell: EmailCell,
  };

  const phone: Column<ClaimantColumnDefinition> = {
    Header: () => (
      <TableHeader>
        {t('shifts.placements.table_headers.phone', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'phone',
    Cell: PhoneCell,
  };

  const employmentTypeMismatch: Column<ClaimantColumnDefinition> = {
    Header: '',
    accessor: 'employmentTypeMismatch',
    Cell: EmploymentTypeMismatchCell,
  };

  const actions: Column<ClaimantColumnDefinition> = {
    accessor: 'actions',
    Header: '',
    Cell: ActionCell,
  };

  const noShow: Column<ClaimantColumnDefinition> = {
    accessor: 'noShow',
    Header: '',
    Cell: NoShowCell,
  };

  return [
    flexWorkerImage,
    flexPoolIndicator,
    fw,
    email,
    phone,
    employmentTypeMismatch,
    noShow,
    actions,
  ];
};

const FlexPoolIndicatorCell = ({ value }: ClaimantCellProps<'flexPoolIndicator'>) => {
  return value ? <IconFlexPoolOnlyJob width={24} /> : null;
};

const EmailCell = ({ value }: ClaimantCellProps<'email'>) => {
  return <TableCell>{value}</TableCell>;
};

const PhoneCell = ({ value }: ClaimantCellProps<'phone'>) => {
  return <TableCell>{value}</TableCell>;
};

const NoShowCell = ({ value }: ClaimantCellProps<'noShow'>) => {
  if (value) {
    return (
      <Typography variant="body1" color="secondary">
        No-show
      </Typography>
    );
  }
  return <></>;
};

const EmploymentTypeMismatchCell = ({ value }: ClaimantCellProps<'employmentTypeMismatch'>) => {
  const { t } = useTranslation('hq-screen-specific');

  if (value) {
    return (
      <Tooltip
        placement="top"
        title={t('shifts.placements.employment_type_not_supported', {
          ns: 'hq-screen-specific',
        })}
      >
        <IconBox data-testid="employment-type-mismatch-warning">
          <StyledIcon as={IconWarning} />
        </IconBox>
      </Tooltip>
    );
  }
  return <></>;
};

const ActionCell = ({ value, getRowActions }: ClaimantCellProps<'actions'>) => {
  const actions = getRowActions(value);

  if (actions.length === 0) {
    return null;
  }

  return (
    <ContextMenuButton menuId={`claimant-actions-menu-${value.workerId}`}>
      {actions}
    </ContextMenuButton>
  );
};

const StyledIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwDanger[100],
    strokeWidth: theme.spacing(0.5),
  },
}));

const IconBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));
